import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajanTiimikokonaisuus } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { ALKU_AIKOJEN_ALUSTA, TiimienHallintaService } from '../tiimien-hallinta.service'

export interface MuokkaaTiimikokonaisuusDialogData {
  kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain']
  nykyinenNimi: string
  nykyinenAlku: NumberDate
  nykyinenLoppu: NumberDate | null
}

interface TiimikokonaisuusLomake {
  nimi: string
  alku: Date | null
  loppu: Date | null
}

@Component({
  templateUrl: './muokkaa-tiimikokonaisuus.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MuokkaaTiimikokonaisuusDialog {
  lomake: FieldTree<TiimikokonaisuusLomake>

  virhe: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MuokkaaTiimikokonaisuusDialogData,
    private _dialogRef: MatDialogRef<MuokkaaTiimikokonaisuusDialog>,
    private _ladataanService: LadataanService,
    private _hallintaService: TiimienHallintaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
  ) {
    const alkuEsitayte = data.nykyinenAlku && data.nykyinenAlku !== ALKU_AIKOJEN_ALUSTA ? this._dateService.numberToDate(data.nykyinenAlku) : null
    this.lomake = form(
      signal<TiimikokonaisuusLomake>({ nimi: data.nykyinenNimi ?? '', alku: alkuEsitayte, loppu: data.nykyinenLoppu ? this._dateService.numberToDate(data.nykyinenLoppu) : null }),
      polku => {
        required(polku.nimi)
      },
    )
  }

  peruuta() {
    this._dialogRef.close()
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      return
    }
    this.virhe = null
    const arvot = this.lomake().value()
    const nimi = (arvot.nimi || '').trim()
    if (!nimi) {
      this.virhe = 'Nimi ei voi olla tyhjä.'
      return
    }
    const alku: NumberDate = arvot.alku ? this._dateService.dateToNumber(arvot.alku) : ALKU_AIKOJEN_ALUSTA
    const loppu: NumberDate | null = arvot.loppu ? this._dateService.dateToNumber(arvot.loppu) : null
    if (loppu !== null && loppu < alku) {
      this.virhe = 'Loppupäivä ei voi olla ennen alkupäivää.'
      return
    }
    try {
      this._ladataanService.aloitaLataaminen()
      await this._hallintaService.paivitaTiimikokonaisuudenTiedot(this.data.kokonaisuusAvain, { nimi, alku, loppu })
      this._snackbar.open('Tiimikokonaisuuden tiedot tallennettu', 'OK', { duration: 5000 })
      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tallennus epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
